import { useState, useEffect, useCallback } from "react";
import API, { BASE_DEFAULT_IMAGE_URL, getloginUserId } from "../api/axios";
import Layoutsecond from "../components/Layoutsecond";
import { Link, useNavigate } from "react-router-dom";
import { SlLike } from "react-icons/sl";
import { FaRegCommentDots } from "react-icons/fa";
import { GoShareAndroid } from "react-icons/go";
import { BiReset } from "react-icons/bi";


export default function Discover() {
  const [feedItems, setFeedItems] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [subSubtopics, setSubSubtopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState("");
  const [selectedSubtopic, setSelectedSubtopic] = useState("");
  const [selectedSubSubtopic, setSelectedSubSubtopic] = useState("");
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareItem, setShareItem] = useState(null);
  const [adsMap, setAdsMap] = useState({});
  const [imageStates, setImageStates] = useState({});
  const [hasSearched, setHasSearched] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [loadingSubtopics, setLoadingSubtopics] = useState(false);
  const [loadingSubSubtopics, setLoadingSubSubtopics] = useState(false);

  const loginUserId = getloginUserId();
  const navigate = useNavigate();
  const isLoggedIn = !!loginUserId;

  // Format published time
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "";
    const now = new Date();
    const past = new Date(timestamp);
    if (isNaN(past.getTime())) return "";
    const diffInSeconds = Math.floor((now - past) / 1000);
    if (diffInSeconds < 60) return `${diffInSeconds}s`;
    const minutes = Math.floor(diffInSeconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(diffInSeconds / 3600);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(diffInSeconds / 86400);
    if (days < 7) return `${days}d`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return `${weeks}w`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo`;
    const years = Math.floor(days / 365);
    return `${years}y`;
  };

  // Fetch topics on mount
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const res = await API.get("/topics");
        if (res.data.success) {
          const Topics = res.data.data.filter((topic) => topic.parent_id === null);
          setTopics(Topics);
        }
      } catch (err) {
        console.error("Failed to fetch topics:", err);
      }
    };
    fetchTopics();
  }, []);

  // Load all feed items on mount (default load)
  useEffect(() => {
    const loadDefaultFeedItems = async () => {
      setLoading(true);
      try {
        const initialUrl = "/feed-items";
        await fetchFeedItems(initialUrl, true);
      } catch (err) {
        console.error("Failed to load default feed items:", err);
      } finally {
        setLoading(false);
      }
    };
    
    loadDefaultFeedItems();
  }, []);

  // Fetch subtopics when topic changes
  useEffect(() => {
    if (selectedTopic) {
      setLoadingSubtopics(true);
      setSubtopics([]);
      setSubSubtopics([]);
      setSelectedSubtopic("");
      setSelectedSubSubtopic("");

      API.get(`/topics/${selectedTopic}/subtopics`)
        .then((res) => {
          if (res.data.success) {
            setSubtopics(res.data.data || []);
          } else {
            setSubtopics([]);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch subtopics:", err);
          setSubtopics([]);
        })
        .finally(() => {
          setLoadingSubtopics(false);
        });
    } else {
      setSubtopics([]);
      setSubSubtopics([]);
      setSelectedSubtopic("");
      setSelectedSubSubtopic("");
    }
  }, [selectedTopic]);

  // Fetch sub-subtopics when subtopic changes
  useEffect(() => {
    if (selectedSubtopic) {
      setLoadingSubSubtopics(true);
      setSubSubtopics([]);
      setSelectedSubSubtopic("");

      API.get(`/topics/${selectedSubtopic}/sub-subtopics`)
        .then((res) => {
          if (res.data.success) {
            setSubSubtopics(res.data.data || []);
          } else {
            setSubSubtopics([]);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch sub-subtopics:", err);
          setSubSubtopics([]);
        })
        .finally(() => {
          setLoadingSubSubtopics(false);
        });
    } else {
      setSubSubtopics([]);
      setSelectedSubSubtopic("");
    }
  }, [selectedSubtopic]);

  // Auto-search effect - triggers whenever any filter changes
  useEffect(() => {
    // Skip if this is the initial mount (handled by default load effect)
    if (!hasSearched && !selectedTopic && !selectedSubtopic && !selectedSubSubtopic) {
      return;
    }

    const performAutoSearch = () => {
      setFeedItems([]);
      setNextPageUrl(null);
      setLoading(true);
      setAdsMap({});
      setImageStates({});
      setHasSearched(true);

      // Determine which topic ID to use (priority: sub-subtopic > subtopic > topic)
      let topicIdToSearch = "";
      if (selectedSubSubtopic) {
        topicIdToSearch = selectedSubSubtopic;
      } else if (selectedSubtopic) {
        topicIdToSearch = selectedSubtopic;
      } else if (selectedTopic) {
        topicIdToSearch = selectedTopic;
      }

      // If no topic selected, load all items
      const initialUrl = topicIdToSearch 
        ? `/feed-items?topic_id=${topicIdToSearch}` 
        : "/feed-items";
      
      fetchFeedItems(initialUrl, true).finally(() => setLoading(false));
    };

    performAutoSearch();
  }, [selectedTopic, selectedSubtopic, selectedSubSubtopic]);

  // Handle image loading with timeout for each feed item
  useEffect(() => {
    feedItems.forEach(feed => {
      if (imageStates[feed.id]?.attempt > 0) return;

      setImageStates(prev => ({
        ...prev,
        [feed.id]: { src: BASE_DEFAULT_IMAGE_URL, attempt: 0, isLoading: false }
      }));

      const startTimeout = setTimeout(() => {
        if (feed.images) {
          setImageStates(prev => ({
            ...prev,
            [feed.id]: { src: feed.images, attempt: 1, isLoading: true }
          }));

          const primaryTimeout = setTimeout(() => {
            setImageStates(prev => {
              const current = prev[feed.id];
              if (current?.attempt === 1 && current?.isLoading) {
                if (feed.source?.logo_url) {
                  return {
                    ...prev,
                    [feed.id]: { src: feed.source.logo_url, attempt: 2, isLoading: true }
                  };
                } else {
                  return {
                    ...prev,
                    [feed.id]: { src: BASE_DEFAULT_IMAGE_URL, attempt: 3, isLoading: false }
                  };
                }
              }
              return prev;
            });
          }, 5500);

          setImageStates(prev => ({
            ...prev,
            [feed.id]: { ...prev[feed.id], primaryTimeout }
          }));

        } else if (feed.source?.logo_url) {
          setImageStates(prev => ({
            ...prev,
            [feed.id]: { src: feed.source.logo_url, attempt: 2, isLoading: true }
          }));

          const fallbackTimeout = setTimeout(() => {
            setImageStates(prev => {
              const current = prev[feed.id];
              if (current?.attempt === 2 && current?.isLoading) {
                return {
                  ...prev,
                  [feed.id]: { src: BASE_DEFAULT_IMAGE_URL, attempt: 3, isLoading: false }
                };
              }
              return prev;
            });
          }, 5500);

          setImageStates(prev => ({
            ...prev,
            [feed.id]: { ...prev[feed.id], fallbackTimeout }
          }));
        }
      }, 50);
    });
  }, [feedItems]);

  // Handle timeout for attempt 2 (fallback image)
  useEffect(() => {
    Object.keys(imageStates).forEach(feedId => {
      const state = imageStates[feedId];
      const feed = feedItems.find(f => f.id === parseInt(feedId));
      
      if (state?.attempt === 2 && state?.isLoading && !state?.fallbackTimeout && feed) {
        const fallbackTimeout = setTimeout(() => {
          setImageStates(prev => {
            const current = prev[feedId];
            if (current?.attempt === 2 && current?.isLoading) {
              return {
                ...prev,
                [feedId]: { src: BASE_DEFAULT_IMAGE_URL, attempt: 3, isLoading: false }
              };
            }
            return prev;
          });
        }, 5500);

        setImageStates(prev => ({
          ...prev,
          [feedId]: { ...prev[feedId], fallbackTimeout }
        }));
      }
    });
  }, [imageStates, feedItems]);

  const fetchFeedItems = async (url, isInitialLoad = false) => {
    if (!url) return;
    setLoadingMore(true);
    try {
      const res = await API.get(url);
      const { success, data } = res.data;
      if (success && data?.data) {
        setFeedItems((prev) => {
          const newItems = data.data.filter(newItem => 
            !prev.some(existingItem => existingItem.id === newItem.id)
          );
          return [...prev, ...newItems];
        });
        setNextPageUrl(data.next_page_url);
        if (isInitialLoad) {
          setHasSearched(true);
        }

        // Fetch random ads for every 15th item
        const currentLength = feedItems.length;
        const adPromises = [];
        
        data.data.forEach((item, idx) => {
          const globalIndex = currentLength + idx;
          if ((globalIndex + 1) % 15 === 0) {
            adPromises.push(
              API.get("/ad-slots/random?placement=feeditem")
                .then(adRes => {
                  if (adRes.data.success && adRes.data.data) {
                    return { index: globalIndex, ad: adRes.data.data };
                  }
                  return null;
                })
                .catch(err => {
                  console.error("Failed to fetch random ad:", err);
                  return null;
                })
            );
          }
        });

        if (adPromises.length > 0) {
          const adResults = await Promise.all(adPromises);
          adResults.forEach(result => {
            if (result && result.ad) {
              setAdsMap(prev => ({ ...prev, [result.index]: result.ad }));
            }
          });
        }
      } else {
        if (isInitialLoad) {
          setHasSearched(true);
        }
      }
    } catch (err) {
      console.error("Feed items fetch failed:", err);
      if (isInitialLoad) {
        setHasSearched(true);
      }
    } finally {
      setLoadingMore(false);
    }
  };

  // Infinite scroll
  const handleScroll = useCallback(() => {
    if (window.scrollY > 400) {
      setShowScrollTop(true);
    } else {
      setShowScrollTop(false);
    }

    if (
      window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 200 &&
      !loadingMore &&
      nextPageUrl
    ) {
      fetchFeedItems(nextPageUrl);
    }
  }, [nextPageUrl, loadingMore]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  const requireLogin = () => {
    if (!isLoggedIn) {
      navigate("/login");
      return false;
    }
    return true;
  };

  // Like
  const handleLike = async (id, index) => {
    if (!requireLogin()) return;
    const updated = [...feedItems];
    const originalLiked = updated[index].is_liked || false;
    const originalCount = updated[index].likes_count || 0;

    updated[index].is_liked = !originalLiked;
    updated[index].likes_count = originalLiked ? Math.max(0, originalCount - 1) : originalCount + 1;
    setFeedItems(updated);

    try {
      const res = await API.post(`/feed-items/${id}/like`, { user_id: loginUserId });
      if (res.data.success) {
        updated[index].is_liked = res.data.liked;
        updated[index].likes_count = res.data.likes_count;
        setFeedItems([...updated]);
      } else {
        updated[index].is_liked = originalLiked;
        updated[index].likes_count = originalCount;
        setFeedItems([...updated]);
      }
    } catch (err) {
      console.error("Like failed:", err);
      updated[index].is_liked = originalLiked;
      updated[index].likes_count = originalCount;
      setFeedItems([...updated]);
    }
  };

  // Share Modal
  const handleShare = (item) => {
    if (!requireLogin()) return;
    setShareItem(item);
    setShowShareModal(true);
  };

  const ShareModal = () => {
    if (!showShareModal || !shareItem) return null;
    const shareUrl = `${window.location.origin}/discover-details/${shareItem.id}`;
    const shareTitle = shareItem.title || "Check this out";

    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert("Link copied to clipboard!");
        setShowShareModal(false);
        if (isLoggedIn) await API.post(`/feed-items/${shareItem.id}/share`, { user_id: loginUserId });
      } catch {
        const textArea = document.createElement("textarea");
        textArea.value = shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        alert("Link copied to clipboard!");
        setShowShareModal(false);
        if (isLoggedIn) API.post(`/feed-items/${shareItem.id}/share`, { user_id: loginUserId });
      }
    };

    const shareOnSocialMedia = (platform) => {
      let socialShareUrl = "";
      switch (platform) {
        case "facebook":
          socialShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
          break;
        case "twitter":
          socialShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`;
          break;
        case "linkedin":
          socialShareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
          break;
        case "whatsapp":
          socialShareUrl = `https://wa.me/?text=${encodeURIComponent(shareTitle + " " + shareUrl)}`;
          break;
        case "telegram":
          socialShareUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
          break;
        default:
          return;
      }
      window.open(socialShareUrl, "_blank", "width=600,height=400");
      setShowShareModal(false);
      if (isLoggedIn) API.post(`/feed-items/${shareItem.id}/share`, { user_id: loginUserId });
    };

    return (
      <div className="modal-backdrop" style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0,0,0,0.5)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000 }}>
        <div className="modal-content" style={{ backgroundColor: "white", padding: "20px", borderRadius: "8px", maxWidth: "400px", width: "90%" }}>
          <div className="modal-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h5 style={{ margin: 0 }}>Share this post</h5>
            <button onClick={() => setShowShareModal(false)} style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer" }}>×</button>
          </div>

          <div className="share-options" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "15px", marginBottom: "20px" }}>
            <button onClick={() => shareOnSocialMedia("facebook")} className="btn btn-primary" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "10px" }}><span style={{ fontSize: "24px", marginBottom: "5px" }}>f</span><small>Facebook</small></button>
            <button onClick={() => shareOnSocialMedia("twitter")} className="btn btn-info" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "10px" }}><span style={{ fontSize: "24px", marginBottom: "5px" }}>𝕏</span><small>Twitter</small></button>
            <button onClick={() => shareOnSocialMedia("linkedin")} className="btn btn-primary" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "10px" }}><span style={{ fontSize: "24px", marginBottom: "5px" }}>in</span><small>LinkedIn</small></button>
            <button onClick={() => shareOnSocialMedia("whatsapp")} className="btn btn-success" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "10px" }}><span style={{ fontSize: "24px", marginBottom: "5px" }}>📱</span><small>WhatsApp</small></button>
            <button onClick={() => shareOnSocialMedia("telegram")} className="btn btn-info" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "10px" }}><span style={{ fontSize: "24px", marginBottom: "5px" }}>✈️</span><small>Telegram</small></button>
            <button onClick={copyToClipboard} className="btn btn-secondary" style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "10px" }}><span style={{ fontSize: "24px", marginBottom: "5px" }}>📋</span><small>Copy Link</small></button>
          </div>

          <div className="share-url" style={{ marginBottom: "15px" }}>
            <label className="small text-muted">Share URL:</label>
            <div style={{ display: "flex", gap: "10px" }}>
              <input type="text" value={shareUrl} readOnly className="form-control" style={{ fontSize: "12px" }} />
              <button onClick={copyToClipboard} className="btn btn-outline-primary" style={{ whiteSpace: "nowrap" }}>Copy</button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Loading Spinner Component
  const LoadingSpinner = () => (
    <div style={{ 
      display: "flex", 
      justifyContent: "center", 
      alignItems: "center", 
      minHeight: "300px",
      flexDirection: "column",
      gap: "15px"
    }}>
      <div style={{
        border: "4px solid #f3f3f3",
        borderTop: "4px solid #3498db",
        borderRadius: "50%",
        width: "50px",
        height: "50px",
        animation: "spin 1s linear infinite"
      }}></div>
      <p style={{ color: "#666", fontSize: "16px" }}>Loading feed...</p>
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );

  // Empty State Component
  const EmptyState = () => {
    const getSelectedTopicName = () => {
      if (selectedSubSubtopic) {
        const subSubtopic = subSubtopics.find(t => t.id === parseInt(selectedSubSubtopic));
        return subSubtopic?.name;
      }
      if (selectedSubtopic) {
        const subtopic = subtopics.find(t => t.id === parseInt(selectedSubtopic));
        return subtopic?.name;
      }
      if (selectedTopic) {
        const topic = topics.find(t => t.id === parseInt(selectedTopic));
        return topic?.name;
      }
      return null;
    };

    const selectedTopicName = getSelectedTopicName();
    
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "400px",
        padding: "60px 20px",
        textAlign: "center",
        backgroundColor: "#f8f9fa",
        borderRadius: "12px",
        margin: "20px 0"
      }}>
        <div style={{
          width: "120px",
          height: "120px",
          backgroundColor: "#e3f2fd",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: "24px",
          position: "relative",
          boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)"
        }}>
          <svg 
            width="60" 
            height="60"   
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="#6366f1" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10"/>
            <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="#6366f1" opacity="0.2"/>
            <line x1="12" y1="2" x2="12" y2="6"/>
            <line x1="12" y1="18" x2="12" y2="22"/>
          </svg>
          
          <div style={{
            position: "absolute",
            bottom: "5px",
            right: "5px",
            width: "36px",
            height: "36px",
            backgroundColor: "#fff",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            border: "2px solid #f8f9fa"
          }}>
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="#adb5bd" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
              <line x1="11" y1="8" x2="11" y2="14"/>
              <line x1="8" y1="11" x2="14" y2="11"/>
            </svg>
          </div>
        </div>

        <h3 style={{ 
          fontSize: "24px", 
          fontWeight: "700", 
          marginBottom: "12px",
          color: "#212529",
          letterSpacing: "-0.5px"
        }}>
          No Feed Items Found
        </h3>

        <p style={{ 
          fontSize: "15px", 
          color: "#6c757d",
          maxWidth: "450px",
          lineHeight: "1.6",
          marginBottom: "8px"
        }}>
          {selectedTopicName 
            ? `We couldn't find any feed items in the "${selectedTopicName}" topic.`
            : "We couldn't find any feed items at the moment."
          }
        </p>
        <p style={{ 
          fontSize: "14px", 
          color: "#adb5bd",
          maxWidth: "400px",
          lineHeight: "1.5",
          marginBottom: "32px"
        }}>
          {selectedTopicName 
            ? "Try exploring other topics or check back later for new content."
            : "Please check back later for new content."
          }
        </p>

        {(selectedTopic || selectedSubtopic || selectedSubSubtopic) && (
          <button
            onClick={() => {
              setSelectedTopic("");
              setSelectedSubtopic("");
              setSelectedSubSubtopic("");
              setSubtopics([]);
              setSubSubtopics([]);
              
              // Reset and reload all items
              setFeedItems([]);
              setNextPageUrl(null);
              setLoading(true);
              setAdsMap({});
              setImageStates({});
              
              const initialUrl = "/feed-items";
              fetchFeedItems(initialUrl, true).finally(() => setLoading(false));
            }}
            className="btn-secondary"
            style={{
              padding: "12px 24px",
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "15px",
              fontWeight: "600",
              letterSpacing: "0.3px",
              transition: "all 0.3s ease",
              boxShadow: "0 4px 12px rgba(108, 117, 125, 0.3)",
              display: "flex",
              alignItems: "center",
              gap: "8px"
            }}
          >
            <svg 
              width="18" 
              height="18" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2.5" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M3 6h18M3 12h18M3 18h18"/>
            </svg>
            Reset Filters
          </button>
        )}
      </div>
    );
  };

  return (
    <Layoutsecond>
      <h2 className="mb-4">Discover</h2>

      {/* Topic Filter with Cascading Dropdowns */}
      <div className="discover-filters mb-4" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px", alignItems: "end" }}>

        {/* Topic */}
        <div>
          <label htmlFor="topic_id" style={{ display: "block", marginBottom: "5px", fontSize: "14px", fontWeight: "500" }}>
            Topic
          </label>
          <select
            id="topic_id"
            value={selectedTopic}
            onChange={(e) => setSelectedTopic(e.target.value)}
            className="border rounded form-select"
            disabled={loading}
          >
            <option value="">All Topics</option>
            {topics.map((topic) => (
              <option key={topic.id} value={topic.id}>
                {topic.name.charAt(0).toUpperCase() + topic.name.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Subtopic */}
        <div>
          <label htmlFor="subtopic_id" style={{ display: "block", marginBottom: "5px", fontSize: "14px", fontWeight: "500" }}>
            Subtopic
          </label>
          <select
            id="subtopic_id"
            value={selectedSubtopic}
            onChange={(e) => setSelectedSubtopic(e.target.value)}
            className="border rounded form-select"
            disabled={!selectedTopic || loadingSubtopics || loading}
          >
            <option value="">
              {loadingSubtopics ? "Loading..." : "Select Subtopic"}
            </option>
            {subtopics.map((subtopic) => (
              <option key={subtopic.id} value={subtopic.id}>
               {subtopic.name.charAt(0).toUpperCase() + subtopic.name.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Sub-Subtopic */}
        <div>
          <label htmlFor="sub_subtopic_id" style={{ display: "block", marginBottom: "5px", fontSize: "14px", fontWeight: "500" }}>
            Sub-Subtopic
          </label>
          <select
            id="sub_subtopic_id"
            value={selectedSubSubtopic}
            onChange={(e) => setSelectedSubSubtopic(e.target.value)}
            className="border rounded form-select"
            disabled={!selectedSubtopic || loadingSubSubtopics || loading}
          >
            <option value="">
              {loadingSubSubtopics ? "Loading..." : "Select Sub-Subtopic"}
            </option>
            {subSubtopics.map((subSubtopic) => (
              <option key={subSubtopic.id} value={subSubtopic.id}>
               {subSubtopic.name.charAt(0).toUpperCase() + subSubtopic.name.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Reset Filters Button */}
        {(selectedTopic || selectedSubtopic || selectedSubSubtopic) && (
          <div>
            <button
              onClick={() => {
                setSelectedTopic("");
                setSelectedSubtopic("");
                setSelectedSubSubtopic("");
                setSubtopics([]);
                setSubSubtopics([]);

                // Reset and reload all items
                setFeedItems([]);
                setNextPageUrl(null);
                setLoading(true);
                setAdsMap({});
                setImageStates({});

                const initialUrl = "/feed-items";
                fetchFeedItems(initialUrl, true).finally(() => setLoading(false));
              }}
              className="btn-secondary"
              title="Reset filters"
              style={{
                padding: "8px 10px",
                backgroundColor: "#6c757d",
                color: "white",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "15px",
                fontWeight: "600",
                transition: "all 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                whiteSpace: "nowrap",
              }}
            >
              <BiReset size={24} style={{ fontWeight: "bold" }} />
            </button>
          </div>
        )}
      </div>

      {/* Loading State - Initial Load */}
      {loading && <LoadingSpinner />}

      {/* Empty State - No Records Found */}
      {!loading && hasSearched && feedItems.length === 0 && <EmptyState />}

      {/* Feed Items + Ads */}
      {feedItems.length > 0 && (
        <ul className="discover-card-main">
          {feedItems.map((feed, index) => {
            const ad = adsMap[index];
            return (
              <>
                <div className="discover-card-wrapper" key={`feed-wrap-${feed.id}-${index}`}>
                  <li className="discover-card">
                    <div className="discover-card-des">
                      <h3 className="card-title">
                        <Link to={`/discover-details/${feed.id}`}>{feed.title}</Link>
                      </h3>
                      <span className="discover-category-title small">
                        {feed.topic?.name || "Uncategorized"}
                      </span>
                      <p>
                        {feed.description?.length > 250
                          ? `${feed.description.slice(0, 250)}...`
                          : feed.description}
                      </p>

                      <div className="discover-card-icon flex gap-2">
                        <Link
                          onClick={() => handleLike(feed.id, index)}
                          className={`like-link ${feed.is_liked ? "active" : ""} flex items-center gap-1`}
                          style={{ background: "none", border: "none", cursor: "pointer" }}
                        >
                          <SlLike /> <span className="small">{feed.likes_count || 0}</span>
                        </Link>

                        <Link to={`/discover-details/${feed.id}`} className="flex items-center gap-1">
                          <FaRegCommentDots /> <span className="small">{feed.comments_count || 0}</span>
                        </Link>

                        <Link
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleShare(feed); }}
                          className="share-link flex items-center gap-1"
                          style={{ background: "none", border: "none", cursor: "pointer" }}
                        >
                          <GoShareAndroid /> <span className="small">Share</span>
                        </Link>
                      </div>
                    </div>

                    <div className="discover-card-img">
                      <span className="time">{formatTimeAgo(feed.created_at)}</span>
                      <Link to={`/discover-details/${feed.id}`}>
                        <img
                          alt={feed.title}
                          src={imageStates[feed.id]?.src || BASE_DEFAULT_IMAGE_URL}
                          onLoad={() => {
                            setImageStates(prev => {
                              const current = prev[feed.id];
                              if (current?.primaryTimeout) clearTimeout(current.primaryTimeout);
                              if (current?.fallbackTimeout) clearTimeout(current.fallbackTimeout);
                              
                              return {
                                ...prev,
                                [feed.id]: { 
                                  ...current, 
                                  isLoading: false,
                                  primaryTimeout: null,
                                  fallbackTimeout: null
                                }
                              };
                            });
                          }}
                          onError={() => {
                            const currentAttempt = imageStates[feed.id]?.attempt || 0;
                            
                            if (currentAttempt === 1 && feed.source?.logo_url) {
                              setImageStates(prev => {
                                const current = prev[feed.id];
                                if (current?.primaryTimeout) clearTimeout(current.primaryTimeout);
                                
                                return {
                                  ...prev,
                                  [feed.id]: { 
                                    src: feed.source.logo_url, 
                                    attempt: 2, 
                                    isLoading: true,
                                    primaryTimeout: null
                                  }
                                };
                              });
                            } else if (currentAttempt === 2 || (currentAttempt === 1 && !feed.source?.logo_url)) {
                              setImageStates(prev => {
                                const current = prev[feed.id];
                                if (current?.fallbackTimeout) clearTimeout(current.fallbackTimeout);
                                
                                return {
                                  ...prev,
                                  [feed.id]: { 
                                    src: BASE_DEFAULT_IMAGE_URL, 
                                    attempt: 3, 
                                    isLoading: false,
                                    fallbackTimeout: null
                                  }
                                };
                              });
                            }
                          }}
                          loading="lazy"
                          style={{
                            transition: "opacity 0.3s ease-in-out",
                            opacity: imageStates[feed.id]?.isLoading ? 0.6 : 1
                          }}
                        />
                      </Link>
                    </div>                  
                  </li>
                </div>

                {ad && (
                  <div className="discover-ad-wrapper" key={`ad-wrap-${ad.id}-${index}`}>
                    <li className="discover-card ad-card" style={{ position: "relative" }}>
                      <div
                        style={{
                          position: "absolute",
                          top: "8px",
                          right: "8px",
                          background: "#4285f4",
                          color: "white",
                          padding: "2px 8px",
                          borderRadius: "3px",
                          fontSize: "10px",
                          fontWeight: "bold",
                          zIndex: 10,
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Sponsored
                      </div>

                      {ad.type === "banner" ? (
                        <a href={ad.target_url || "#"} target="_blank" rel="noopener noreferrer">
                          <img
                            src={ad.image_url || BASE_DEFAULT_IMAGE_URL}
                            alt={ad.title || "Sponsored Ad"}
                            onError={(e) => (e.currentTarget.src = BASE_DEFAULT_IMAGE_URL)}
                            style={{
                              display: "block",
                              margin: "0 auto",
                              maxWidth: "100%",
                              borderRadius: "8px",
                            }}
                          />
                        </a>
                      ) : ad.type === "embedded" ? (
                        <div
                          className="embedded-ad"
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                            minHeight: "200px",
                            overflow: "hidden",
                          }}
                          dangerouslySetInnerHTML={{ __html: ad.embed_code || "" }}
                        />
                      ) : null}
                    </li>
                  </div>
                )}
              </>
            );
          })}
        </ul>
      )}

      {showShareModal && <ShareModal />}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          style={{
            position: "fixed",
            bottom: "30px",
            right: "30px",
            width: "47px",
            height: "47px",
            backgroundColor: "#5A4CFF",
            color: "white",
            border: "none",
            borderRadius: "50%",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 4px 16px rgba(99, 102, 241, 0.4)",
            transition: "all 0.3s ease",
            zIndex: 999,
            animation: "fadeInUp 0.3s ease"
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = "#4f46e5";
            e.currentTarget.style.transform = "translateY(-4px)";
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(99, 102, 241, 0.5)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = "#6366f1";
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 4px 16px rgba(99, 102, 241, 0.4)";
          }}
          aria-label="Scroll to top"
        >
          <svg 
            width="24" 
            height="24" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2.5" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <path d="M18 15l-6-6-6 6"/>
          </svg>
        </button>
      )}

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </Layoutsecond>
  );
} 